import * as React from 'react';

import { cn } from '../../../lib/utils';
import type { TextFieldProps } from './types';

// The outer box owns the border and focus ring; the inner input is transparent
// so leading/trailing icons sit inside the same bordered surface.
export const TextField = React.forwardRef<HTMLInputElement, TextFieldProps>(
  (
    {
      label,
      helperText,
      error,
      leadingIcon,
      trailingIcon,
      containerClassName,
      inputClassName,
      className,
      id,
      ...props
    },
    ref,
  ) => (
    <div className={cn('flex flex-col gap-2', containerClassName)}>
      {label ? (
        <label htmlFor={id} className="text-sm text-text-tertiary">
          {label}
        </label>
      ) : null}
      <div className="flex flex-col items-start justify-center gap-2">
        <div
          className={cn(
            'flex h-8 w-full items-center gap-2 rounded-8 border bg-control px-2 py-1 transition-colors has-[:focus-visible]:border-focus',
            error ? 'border-control-border-error' : 'border-input',
            inputClassName,
          )}
        >
          {leadingIcon ? (
            <span className="flex size-5 shrink-0 items-center justify-center text-control-placeholder">
              {leadingIcon}
            </span>
          ) : null}
          <input
            ref={ref}
            id={id}
            aria-invalid={error || undefined}
            className={cn(
              'min-w-0 flex-1 bg-transparent text-sm text-text-primary outline-none placeholder:text-input-placeholder disabled:cursor-not-allowed disabled:text-control-disabled-foreground',
              className,
            )}
            {...props}
          />
          {trailingIcon ? <span className="flex shrink-0 items-center">{trailingIcon}</span> : null}
        </div>
        {helperText ? (
          <p
            className={cn(
              'text-xs',
              error ? 'text-button-destructive-foreground' : 'text-text-tertiary',
            )}
          >
            {helperText}
          </p>
        ) : null}
      </div>
    </div>
  ),
);
TextField.displayName = 'TextField';
